import { useMemo } from 'react';
import { useQueryIntegrationPlaybooks } from '@ion/api';
import useGetBulkIntegrations from './useGetBulkIntegrations';
import { getClusterIdsFromPlaybooks, mergePlaybookRevisions } from './utils';

/**
 * Combine hasura integration playbooks (staged rev) with control api
 * integrations (deployed rev) so that both revisions are available.
 */
function useQueryIntPlaybooksWithRevs({ integrationId }) {
  const [getBulkIntegrations, { loading: controlLoading, error: controlError, data: controlData }] =
    useGetBulkIntegrations();

  // Step 1: Fetch integration data from hasura
  const {
    data: hasuraData,
    loading: hasuraLoading,
    isAllowed,
    error: hasuraError,
  } = useQueryIntegrationPlaybooks({
    variables: { integrationId },
    onCompleted: handleOnCompleted,
  });

  // Step 2: After query completes, extra clusterIds and fetch integrations from control API
  function handleOnCompleted(responseData) {
    const playbooks = Array.isArray(responseData?.integrations_by_pk.playbooks)
      ? responseData.integrations_by_pk.playbooks
      : [];

    const clusterIds = getClusterIdsFromPlaybooks(playbooks);

    if (clusterIds.length) {
      getBulkIntegrations({ clusterIds });
    }
  }

  // Step 3: Combine hasura staged revs and control deployed revs
  const mergedData = useMemo(
    () =>
      mergePlaybookRevisions({
        ionIntegrationResponse: hasuraData,
        controlIntegrationResponse: controlData,
      }),
    [hasuraData, controlData]
  );

  return {
    data: mergedData,
    loading: hasuraLoading || controlLoading,
    error: hasuraError || controlError,
    isAllowed,
  };
}

export default useQueryIntPlaybooksWithRevs;
